<template>
  <section id="hero" ref="heroSection" class="hero">
    <div class="container hero__container">
      <div class="hero__grid">
        <!-- Left column: profile image + intro -->
        <div class="hero__left">
          <div class="profile-container">
            <div class="profile-backdrop"/>
            <div class="profile-frame">
              <!-- Eager and high priority, unlike everything further along the
                   track: this is the largest thing in the first viewport and
                   therefore what the page is judged on. -->
              <!-- Cross-fade: the skeleton fades out as the picture fades in,
                   so the frame is never briefly empty between the two. -->
              <Transition name="avatar-fade">
                <div v-if="!avatarReady" class="profile-skeleton" aria-hidden="true" />
              </Transition>
              <img
                v-if="avatar"
                :src="avatar"
                :alt="`${personalInfo.fullName} profile`"
                class="profile-image"
                :class="{ 'is-ready': avatarReady }"
                fetchpriority="high"
                decoding="async"
                @load="onAvatarLoad"
                @error="onAvatarError"
              >
              <div class="profile-glow"/>
            </div>
          </div>

          <div class="hero__text-content">
            <!-- Sized here as well as in the stylesheet. The rule gives it a
                 width and leaves the height to the picture, which the browser
                 does not know until the file arrives: until then the box is the
                 right width and no height at all, and the whole column steps
                 down when it lands. These two attributes are where the shape
                 comes from before the bytes do. -->
            <img
              src="/android-chrome-512x512.png"
              :alt="`${personalInfo.fullName} logo`"
              class="hero__logo animate-in"
              width="512"
              height="512"
              decoding="async"
            >
            <h2 class="hero__name animate-in">{{ personalInfo.name }}</h2>
            <h1 class="hero__title animate-in">Full Stack Engineer</h1>

            <span class="hero__resume-wrap animate-in">
              <!-- Switched off in the admin, this becomes a button that explains
                   itself rather than disappearing. A link that vanishes reads as
                   a site that has nothing to show. -->
              <component
                :is="resumeAvailable ? 'a' : 'button'"
                :href="resumeAvailable ? resumeUrl : undefined"
                :target="resumeAvailable ? '_blank' : undefined"
                :rel="resumeAvailable ? 'noopener noreferrer' : undefined"
                :type="resumeAvailable ? undefined : 'button'"
                class="hero__resume"
                @click="resumeAvailable ? undefined : (askingForResume = true)"
              >
                <span>View Résumé</span>
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="7" y1="17" x2="17" y2="7"/>
                  <polyline points="8 7 17 7 17 16"/>
                </svg>
              </component>
            </span>

            <ResumeRequestModal :open="askingForResume" @close="askingForResume = false" />
          </div>

          <!-- Stacked, the band belongs to this group and not below it: centred
               with the portrait and the title it moves them up by half its own
               height, where sitting underneath as a sibling moved them by all of
               it and left the grid's gap under the resume button as well.

               Both copies are in the page and CSS picks between them. Choosing
               in JavaScript meant the server rendered one and the browser swapped
               to the other after hydration, so the band vanished and came back
               and the hero jumped as this group's height changed. Which one
               belongs here is a question about the viewport, and CSS can answer
               it before anything is painted. -->
          <HomeSkillsMarquee class="hero__band" />

        </div>

        <HomeSkillsMarquee class="hero__aside" />

        <!-- Right column: About Me -->
        <div id="about" class="hero__right">
          <div class="section-heading">
            <div class="heading-line"/>
            <h2 class="section-title">About Me</h2>
          </div>

          <div class="about-bio">
            <p v-for="(paragraph, index) in bioParagraphs" :key="index">{{ paragraph }}</p>
          </div>

          <div class="fun-facts-section">
            <h3 class="facts-title">Fun Facts</h3>
            <ul class="facts-list">
              <li v-for="(fact, index) in personalInfo.funFacts" :key="index" class="fact-item">
                <div class="fact-icon" :class="`icon-${index}`">
                  <svg v-if="index === 0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                  </svg>
                  <svg v-if="index === 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="7"/>
                    <circle cx="12" cy="12" r="3"/>
                    <line x1="12" y1="5" x2="12" y2="3"/>
                    <line x1="17" y1="7" x2="19" y2="5"/>
                    <line x1="19" y1="12" x2="21" y2="12"/>
                    <line x1="17" y1="17" x2="19" y2="19"/>
                    <line x1="12" y1="19" x2="12" y2="21"/>
                    <line x1="7" y1="17" x2="5" y2="19"/>
                    <line x1="5" y1="12" x2="3" y2="12"/>
                    <line x1="7" y1="7" x2="5" y2="5"/>
                  </svg>
                  <svg v-if="index === 2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                    <line x1="8" y1="21" x2="16" y2="21"/>
                    <line x1="12" y1="17" x2="12" y2="21"/>
                    <path d="M10 8l6 4-6 4V8z"/>
                  </svg>
                </div>
                <span class="fact-content">{{ fact }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- Outside .hero__container on purpose: that is a max-width .container, so
         a cue positioned inside it stops at the gutter instead of the panel
         edge. Decorative, so it stays out of the accessibility tree. -->
    <div class="hero__scroll-cue" aria-hidden="true">
      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="6 9 12 15 18 9"/>
      </svg>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { gsap } from 'gsap';
import { personalInfo } from '~/data/content';

// The resume can be switched off from the admin, in which case the button asks
// for it instead of opening it.
const { url: resumeUrl, available: resumeAvailable } = useResume();
const askingForResume = ref(false);

// The portrait, replaceable from the admin. The committed file is the fallback,
// so the frame is never empty — including when the uploaded picture is named by
// the database but will not load, which is what happens under `netlify dev`.
const {
  src: avatar,
  ready: avatarReady,
  onLoad: onAvatarLoad,
  onError: onAvatarError,
} = useAvatar();

// References to DOM elements
const heroSection = ref(null);

const bioParagraphs = computed(() =>
  personalInfo.bio
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
);

// Colour-sweep reveal for each fun fact.
//
// The text itself is never altered; only its colour animates, washing to the
// accent letter by letter and settling back. The characters stay in their own
// spans purely so the sweep has something to stagger across.
//
// Building the spans is structural and happens once; playing the sweep is kept
// separate so it can be replayed. On desktop the fun facts are on screen from
// the start, so a single run at mount is all it needs. On mobile they sit well
// below the fold, where a mount-time run would have finished long before anyone
// scrolled down to it, so there it replays on every entry instead.
let factTimeouts = [];
let factSpanGroups = [];
let factsObserver = null;
let factsBreakpoint = null;

function stopFactsSweep() {
  factTimeouts.forEach(clearTimeout);
  factTimeouts = [];

  // Any half-finished run is rewound, so a replay always starts from the
  // original colour rather than whatever frame it was cut off on.
  factSpanGroups.forEach((spans) => {
    gsap.killTweensOf(spans);
    gsap.set(spans, { clearProps: 'color' });
  });
}

function playFactsSweep() {
  stopFactsSweep();

  factSpanGroups.forEach((spans, index) => {
    // Stagger the start of each fact's animation
    factTimeouts.push(setTimeout(() => {
      gsap.to(spans, { color: 'var(--accent)', stagger: 0.02, duration: 0.2 });

      factTimeouts.push(setTimeout(() => {
        gsap.to(spans, { color: 'var(--secondary)', stagger: 0.01, duration: 0.3 });
      }, 1500));
    }, index * 300));
  });
}

// Splits each fact into per-character spans, grouped into words.
//
// Each character needs `display: inline-block` to be animatable, which also
// makes it an atomic inline box the browser may wrap after — so a flat run of
// spans let lines break mid-word ("tha / t turns"). Wrapping each word in a
// nowrap box confines breaks to the real spaces between words.
function buildFactSpans() {
  factSpanGroups = [];

  document.querySelectorAll('.fact-content').forEach((text) => {
    const originalText = text.textContent;
    text.textContent = '';

    originalText.split(/(\s+)/).forEach((chunk) => {
      if (chunk === '') return;

      if (/^\s+$/.test(chunk)) {
        // A plain text node, so this is the only place a line may break.
        text.appendChild(document.createTextNode(' '));
        return;
      }

      const word = document.createElement('span');
      word.className = 'fact-word';

      chunk.split('').forEach((char) => {
        const span = document.createElement('span');
        span.textContent = char;
        span.style.display = 'inline-block';
        span.dataset.original = char;
        word.appendChild(span);
      });

      text.appendChild(word);
    });

    // Only the character spans — the word wrappers carry no `data-original`.
    factSpanGroups.push(Array.from(text.querySelectorAll('span[data-original]')));
  });
}

function teardownFactsObserver() {
  factsObserver?.disconnect();
  factsObserver = null;
}

// Replays on every entry. Leaving resets the text, so the next entry always
// starts from a clean state rather than resuming mid-scramble.
function observeFactsSection() {
  const section = document.querySelector('.hero .fun-facts-section');
  if (!section || factsObserver) return;

  factsObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) playFactsSweep();
        else stopFactsSweep();
      });
    },
    // Low threshold so it still fires if the section is ever taller than the
    // viewport, in which case a high one could never be satisfied.
    { threshold: 0.2 }
  );

  factsObserver.observe(section);
}

function applyFactsMode() {
  if (factsBreakpoint.matches) {
    observeFactsSection();
  } else {
    teardownFactsObserver();
    playFactsSweep();
  }
}

function setupFactsReveal() {
  buildFactSpans();

  // Matches the layout breakpoint where the hero stacks and the fun facts drop
  // below the fold.
  factsBreakpoint = window.matchMedia('(max-width: 768px)');
  factsBreakpoint.addEventListener('change', applyFactsMode);
  applyFactsMode();
}

// 3D tilt on the portrait.
//
// This used to be two gsap.to calls on every mousemove, each a half-second
// eased tween created and overwritten continuously. Now the pointer position is
// coalesced into one rAF per frame that writes two custom properties, and CSS
// does the transform on the compositor.
let tiltEl = null;
let tiltRaf = null;
let tiltPos = null;

function applyTilt() {
  tiltRaf = null;
  if (!tiltEl || !tiltPos) return;

  const frame = tiltEl.querySelector('.profile-frame');
  const glow = tiltEl.querySelector('.profile-glow');

  if (frame) {
    frame.style.setProperty('--tilt-x', `${(-tiltPos.y * 7).toFixed(2)}deg`);
    frame.style.setProperty('--tilt-y', `${(tiltPos.x * 7).toFixed(2)}deg`);
  }
  if (glow) {
    glow.style.setProperty('--glow-x', `${(tiltPos.x * 14).toFixed(1)}px`);
    glow.style.setProperty('--glow-y', `${(tiltPos.y * 14).toFixed(1)}px`);
    glow.style.opacity = '0.8';
  }
}

function onTiltMove(event) {
  if (!tiltEl) return;
  const r = tiltEl.getBoundingClientRect();
  tiltPos = {
    x: ((event.clientX - r.left) / r.width - 0.5) * 2,
    y: ((event.clientY - r.top) / r.height - 0.5) * 2,
  };
  if (!tiltRaf) tiltRaf = requestAnimationFrame(applyTilt);
}

function resetTilt() {
  if (tiltRaf) {
    cancelAnimationFrame(tiltRaf);
    tiltRaf = null;
  }
  if (!tiltEl) return;

  const frame = tiltEl.querySelector('.profile-frame');
  const glow = tiltEl.querySelector('.profile-glow');

  frame?.style.setProperty('--tilt-x', '0deg');
  frame?.style.setProperty('--tilt-y', '0deg');
  if (glow) {
    glow.style.setProperty('--glow-x', '0px');
    glow.style.setProperty('--glow-y', '0px');
    glow.style.opacity = '';
  }
}

function setupProfileTilt() {
  // Pointer devices only: a phone has nothing to follow, and :hover sticks to
  // whatever was tapped last.
  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  tiltEl = document.querySelector('.profile-container');
  if (!tiltEl) return;

  tiltEl.addEventListener('mousemove', onTiltMove, { passive: true });
  tiltEl.addEventListener('mouseleave', resetTilt);
}

onMounted(() => {
  // Delayed so the spans are built against settled layout.
  setTimeout(setupFactsReveal, 100);
  setupProfileTilt();
});

onUnmounted(() => {
  gsap.killTweensOf('.fact-content span');
  stopFactsSweep();
  teardownFactsObserver();
  factsBreakpoint?.removeEventListener('change', applyFactsMode);

  tiltEl?.removeEventListener('mousemove', onTiltMove);
  tiltEl?.removeEventListener('mouseleave', resetTilt);
  if (tiltRaf) cancelAnimationFrame(tiltRaf);
  tiltEl = null;
});
</script>

<style lang="scss" scoped>
@font-face {
  font-family: 'MBFSpace';
  src: url('~/assets/fonts/MBFSpace/MBFSpace.ttf') format('truetype');
  font-weight: normal;
  font-style: normal;
  font-display: swap;
}

.hero {
  position: relative;
  height: 100%; /* fills its .h-scroll-panel ancestor (100vh) */
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: transparent;
  user-select: none; /* Make all text unselectable */
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;

  &__container {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    z-index: 2;
    width: 100%;
    // The horizontal half is not zero. The shorthand here overrides the 2rem
    // that `.container` gives every other section, so the hero alone ran its
    // columns flat against the container edge.
    padding: clamp(1rem, 4vh, 2.5rem) 2rem;

    // And more than that through the middle sizes, which is where the row is
    // tightest. From 1101 the marquee appears and the gap widens to clear it,
    // so the two columns at their maximums plus that gap want about 1120px,
    // while an 88vw container at a 1280 screen offers 1126. Six pixels of
    // slack, and it reads as it measures.
    //
    // The columns can afford to give it back: the portrait is sized in
    // viewport units and the other column is prose.
    @media (min-width: 769px) and (max-width: 1440px) {
      padding-inline: clamp(1.5rem, 3vw, 3rem);
    }

    // No top padding on mobile: the first column sizes itself to the viewport
    // and centres in it, so any padding here would shift that centre down.
    //
    // The horizontal padding has to be restored explicitly. The shorthand above
    // zeroes it, which overrides the global `.container` mobile padding and left
    // the About column sitting 16px further left than every other section.
    @media (max-width: 768px) {
      padding-top: 0;
      padding-left: 1rem;
      padding-right: 1rem;
    }
  }

  // Left/right columns are leveled to the same height and spaced apart from
  // each other; each column's own content is centered within it. `center`
  // (not `space-between`) keeps the gap pinned to the `gap` value itself
  // rather than letting leftover viewport width inflate it.
  //
  // The marquee between them is a zero-width anchor (see HomeSkillsMarquee),
  // so above 1100px the visible space between the two columns is 2 × gap —
  // one on each side of it — and the marquee floats centred in that space
  // without occupying any of it. Below 1100px the marquee is display:none
  // and the space is simply 1 × gap.
  &__grid {
    display: flex;
    justify-content: center;
    align-items: stretch;
    gap: clamp(1rem, 2.5vw, 2.5rem);
    width: 100%;
    height: 100%;
    position: relative;
    z-index: 10;

    // From the marquee's breakpoint up, the gap must clear the floating
    // marquee; past that, wider screens get progressively more breathing
    // room between the two columns.
    // The gap is not empty: the marquee floats in it, about 93px of icons at
    // this size. At the old 5rem a side that left roughly 33px of clear space
    // between the portrait and the icons, and the same again between the icons
    // and the text, which is what read as crowded. This is about 102px a side
    // at 1280, so the clear space either side of the marquee roughly doubles.
    //
    // The columns still fit: the portrait is sized in viewport units and only
    // wants about 282px at this width, and the prose caps at 540px.
    @media (min-width: 1101px) {
      gap: clamp(5.5rem, 8vw, 9rem);
    }

    // Raised to match, or crossing this boundary would narrow the gap rather
    // than widen it.
    @media (min-width: 1500px) {
      gap: clamp(8rem, 8.5vw, 10rem);
    }

    @media (min-width: 1800px) {
      gap: 10rem;
    }

    @media (max-width: 768px) {
      flex-direction: column;
      height: auto;
      gap: 2.5rem;
    }
  }

  &__left {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    max-width: 420px;
    gap: clamp(0.75rem, 2.5vh, 1.5rem);

    // On mobile this column is the whole first screen, so it gets the whole
    // viewport and centres inside it rather than sitting flush against the top
    // edge.
    //
    // `svh`, and the unit matters more than anything else in this block.
    //
    // This was `dvh`, chosen so the collapsing address bar could not push the
    // centre off-screen. It does prevent that, and it also does exactly what
    // its name says: the *dynamic* viewport height tracks the browser UI
    // continuously, so every frame of the address bar sliding away changed this
    // column's height, re-centred everything inside it, and moved the scroll
    // cue anchored to its bottom. A full re-layout of the first screen on every
    // frame of a transition the browser was trying to run smoothly.
    //
    // `svh` is the *small* viewport: the height with the browser UI showing. It
    // is a fixed number that does not move while the toolbar does, and because
    // it is the smaller of the two, content still fits when the bar is visible,
    // which was the whole point of not using plain `vh`.
    //
    // Never `dvh` for anything that lays out. It is for the rare case that
    // genuinely wants to track the UI, and the cost is a reflow per frame.
    @media (max-width: 768px) {
      height: auto;
      min-height: 100vh;
      min-height: 100svh;
      justify-content: center;
      max-width: 100%;
      gap: 1.5rem;
      position: relative; // anchors the scroll cue to the bottom of the screen
    }
  }

  &__right {
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 100%;
    max-width: 540px;
    gap: clamp(0.75rem, 2.5vh, 1.5rem);

    // Left-aligned on mobile, matching the desktop column. Centred prose sets
    // a ragged left edge that the eye has to re-find on every line, and the
    // fun-facts rows read as a list, which wants a straight edge to hang off.
    @media (max-width: 768px) {
      height: auto;
      align-items: stretch;
      text-align: left;
      max-width: 100%;

      // Explicit margins rather than a flex gap. The gap stacked on top of the
      // heading's own margin-bottom, putting 36px between the heading and the
      // bio where every other section uses ~10px.
      gap: 0;

      .section-heading {
        margin-bottom: clamp(0.5rem, 1.2vh, 0.75rem);
      }

      .about-bio, .fun-facts-section {
        width: 100%;
        max-width: 500px;
        margin-inline: 0;
      }

      // Mirrors the subtitle-to-content gap the other sections use.
      .about-bio {
        margin-bottom: clamp(1rem, 2.5vh, 2.25rem);
      }
    }
  }

  &__text-content {
    text-align: center;
    position: relative;
    flex-shrink: 0;

    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 150%;
      height: 150%;
      background: radial-gradient(
        ellipse at center,
        rgba(var(--accent-rgb), 0.05) 0%,
        rgba(var(--accent-rgb), 0) 70%
      );
      pointer-events: none;
      z-index: -1;
    }
  }

  &__logo {
    display: block;
    width: clamp(34px, 2.4vw + 1.6vh, 52px);
    height: auto;
    margin: 0 auto clamp(0.5rem, 1.4vh, 0.9rem);
    // The mark is white line-art on transparency, so it would be invisible on
    // the light theme. brightness(0) flattens it to black while preserving the
    // alpha channel; dark mode gets the original white.
    filter: brightness(0);
    opacity: 0.75;
  }

  &__name {
    font-size: clamp(0.85rem, 1.05vw + 0.65vh, 1.25rem);
    font-weight: 400;
    letter-spacing: 0.3em;
    margin-bottom: 0.6em;
    opacity: 0.8;
    text-transform: uppercase;
    font-family: 'MBFSpace', monospace;
  }

  &__title {
    font-size: clamp(0.8rem, 0.5vw + 0.55vh, 1.05rem);
    font-weight: 700;
    line-height: 1.1;
    letter-spacing: 0.12em;
    margin-bottom: 0.5em;
    font-family: 'Orbitron', 'Times New Roman', serif;
    color: transparent;
    background: linear-gradient(
      135deg,
      var(--text) 0%,
      var(--accent) 50%,
      var(--text) 100%
    );
    background-size: 200% auto;
    background-clip: text;
    -webkit-background-clip: text;
    text-shadow: 0 0 5px rgba(var(--accent-rgb), 0.3);
  }

  // Carries the entrance animation and the spacing, so the control inside it
  // is free to change between an anchor and a button without losing either.
  &__resume-wrap {
    display: inline-flex;
    margin-top: clamp(0.85rem, 2.2vh, 1.4rem);
  }

  &__resume {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    padding: 0.35rem 0;
    // A <button> when the file is switched off, which otherwise arrives with
    // its own border, background and font.
    border: none;
    background: none;
    font: inherit;
    font-size: clamp(0.75rem, 0.3vw + 0.6vh, 0.88rem);
    font-weight: 600;
    letter-spacing: 0.5px;
    // No border and no underline: the accent colour alone carries the
    // affordance, backed by the arrow glyph and the cursor. Hover deepens the
    // colour and nudges the arrow so there is still live feedback.
    color: var(--accent);
    text-decoration: none;
    cursor: pointer;
    transition: color 0.2s ease-in-out, opacity 0.2s ease-in-out;

    svg {
      transition: transform 0.2s ease-in-out;
    }

    &:hover,
    &:focus-visible {
      opacity: 0.75;
      text-decoration: none;

      svg {
        transform: translate(2px, -2px);
      }
    }
  }

  &__tagline {
    font-size: clamp(0.78rem, 0.45vw + 0.6vh, 0.95rem);
    font-weight: 400;
    // Sentence case: it's a sentence, and the wide tracking that suited all
    // caps hurts readability once it's set normally.
    letter-spacing: 0.01em;
    line-height: 1.6;
    opacity: 0.75;
  }

}

// The class stays in the markup but no longer hides anything: the entrance
// tween that used to reveal these is gone.
.animate-in {
  opacity: 1;
}

:root.dark .hero__logo {
  filter: none;
  opacity: 0.85;
}

// Section heading (shared style with other sections)
.section-heading {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.75em;
  flex-shrink: 0;

  .heading-line {
    // Hidden site-wide. Kept in the markup and styled so it can be brought
    // back by dropping this one line. display:none rather than opacity/width so
    // the flex gap beside it collapses too and the title sits flush left.
    display: none;
    width: 50px;
    height: 3px;
    flex-shrink: 0;
    // Tapered to a point at both ends and faded out, so it reads as a mark
    // rather than a hard bar. The clip gives the sharp tips; the gradient
    // stops the tips ending abruptly.
    background: linear-gradient(
      90deg,
      rgba(var(--accent-rgb), 0) 0%,
      var(--accent) 40%,
      var(--accent) 60%,
      rgba(var(--accent-rgb), 0) 100%
    );
    clip-path: polygon(0 50%, 14% 0, 86% 0, 100% 50%, 86% 100%, 14% 100%);

    @media (max-width: 1280px) {
      width: 40px;
    }
  }

  .section-title {
    font-size: clamp(1.4rem, 1.6vw + 1.6vh, 2.25rem);
    font-weight: 800;
    color: var(--text);
    margin: 0;
    font-family: 'Orbitron', sans-serif;
    letter-spacing: 4px;
    background: linear-gradient(135deg, var(--accent) 0%, rgba(var(--accent-rgb), 0.8) 50%, var(--text) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    text-transform: uppercase;
  }
}

.about-bio {
  font-size: clamp(0.8rem, 0.5vw + 1vh, 1.05rem);
  line-height: 1.6;
  color: var(--secondary);

  p {
    margin: 0 0 0.75em;
  }

  p:last-child {
    margin-bottom: 0;
  }
}

.fun-facts-section {
  width: 100%;
  position: relative;
  flex-shrink: 0;
}

.facts-title {
  font-size: clamp(0.95rem, 0.4vw + 0.8vh, 1.1rem);
  margin-bottom: 0.75em;
  position: relative;
  color: var(--text);
  font-weight: 600;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;

  // Same tapered mark as the section headings, scaled down.
  &::before {
    display: none; // hidden site-wide alongside the section heading dashes
    content: '';
    width: 18px;
    height: 3px;
    margin-right: 0.6rem;
    flex-shrink: 0;
    background: linear-gradient(
      90deg,
      rgba(var(--accent-rgb), 0) 0%,
      var(--accent) 40%,
      var(--accent) 60%,
      rgba(var(--accent-rgb), 0) 100%
    );
    clip-path: polygon(0 50%, 14% 0, 86% 0, 100% 50%, 86% 100%, 14% 100%);
  }
}

.facts-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: clamp(0.5rem, 1.2vh, 1rem);
}

.fact-item {
  display: flex;
  align-items: center;
  position: relative;
  transition: all 0.3s ease;

  &:hover {
    transform: translateX(5px);

    .fact-icon {
      background-color: rgba(var(--accent-rgb), 0.15);
      transform: scale(1.1);

      svg {
        stroke: var(--accent);
      }
    }
  }

  @media (max-width: 1280px) {
    font-size: 0.9rem;
  }
}

.fact-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background:
    // Layered over the page colour rather than left translucent. At 10% alpha
    // the HoneycombField backdrop reads straight through the tile, which looks
    // like the comb is painted on top of it. Renders identically, but opaque.
    linear-gradient(rgba(var(--accent-rgb), 0.1), rgba(var(--accent-rgb), 0.1)),
    var(--background);
  border-radius: 10px;
  margin-right: 1rem;
  transition: all 0.3s ease;
  flex-shrink: 0;

  svg {
    width: 20px;
    height: 20px;
    stroke: rgba(var(--accent-rgb), 0.8);
    transition: all 0.3s ease;
  }

  @media (max-width: 1280px) {
    width: 36px;
    height: 36px;
    margin-right: 0.75rem;

    svg {
      width: 18px;
      height: 18px;
    }
  }
}

// Scroll affordance. Desktop shows both columns at once so the page is
// self-evidently wider than the screen; only the mobile layout, where the first
// column fills the viewport exactly, needs telling.
// One of these is in the page at any width, and neither waits for JavaScript to
// find out which. See the note beside them in the template.
.hero__band {
  display: none;
}

@media (max-width: 768px) {
  .hero__band {
    display: block;
  }

  .hero__aside {
    display: none;
  }
}

.hero__scroll-cue {
  display: none;

  @media (max-width: 768px) {
    display: flex;
    justify-content: center;
    position: absolute;
    left: 0;
    right: 0;
    // Measured down from the top of the panel, not up from the bottom: the
    // section is taller than the screen here (About Me sits below), so `bottom`
    // put the cue at the end of the whole section instead of the first screen.
    top: calc(100svh - 4.5rem);
    color: var(--muted);
    pointer-events: none; // never intercepts a tap meant for the page

    // The bob lives on the svg, not the container, so it can't fight the
    // container's own positioning. transform/opacity only, so it stays on the
    // compositor and off the main thread.
    svg {
      animation: hero-scroll-cue 2s ease-in-out infinite;
    }
  }

  // Desktop travels sideways, so the same chevron is turned to point that way
  // and bobs along the axis the reader actually scrolls.
  @media (min-width: 769px) {
    display: flex;
    align-items: center;
    position: absolute;
    right: clamp(0.35rem, 0.9vw, 1rem);
    top: 50%;
    transform: translateY(-50%);
    color: var(--muted);
    pointer-events: none;

    svg {
      width: clamp(34px, 3.2vw, 52px);
      height: clamp(34px, 3.2vw, 52px);
      transform: rotate(-90deg);
      animation: hero-scroll-cue-x 2s ease-in-out infinite;
    }
  }
}

@keyframes profile-skeleton-sweep {
  from { background-position: 180% 0; }
  to { background-position: -80% 0; }
}

.avatar-fade-leave-active {
  transition: opacity 0.5s ease;
}

.avatar-fade-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .profile-skeleton { animation: none; }

  .profile-image,
  .avatar-fade-leave-active {
    transition: none;
  }
}

@keyframes hero-scroll-cue {
  0%, 100% { transform: translateY(0); opacity: 0.35; }
  50% { transform: translateY(7px); opacity: 0.85; }
}

// The rotation has to be repeated in every keyframe: an animated transform
// replaces the declared one outright rather than composing with it, so without
// this the arrow would snap back to pointing down as soon as it ran.
@keyframes hero-scroll-cue-x {
  0%, 100% { transform: rotate(-90deg) translateY(0); opacity: 0.35; }
  50% { transform: rotate(-90deg) translateY(11px); opacity: 0.85; }
}

@media (prefers-reduced-motion: reduce) {
  .hero__scroll-cue svg {
    animation: none;
    opacity: 0.6;
  }
}

.fact-content {
  position: relative;

  // Both of these nodes are built in setupFactsReveal via createElement, so
  // they never get the scoped-style attribute and a plain selector silently
  // matches nothing. :deep() is required.

  // Word wrapper: keeps the per-character spans inside it on one line, so text
  // only ever breaks at a real space.
  :deep(.fact-word) {
    display: inline-block;
    white-space: nowrap;
  }

  // Character spans only — lifting the whole word wrapper as well would double
  // the offset on hover.
  //
  // `hover: hover` rather than a width query: on touch a :hover state latches
  // on tap and stays stuck until you tap elsewhere, so a per-character lift
  // leaves single letters hanging out of the line.
  @media (hover: hover) {
    :deep(span[data-original]) {
      transition: transform 0.2s ease;

      &:hover {
        transform: translateY(-2px);
      }
    }
  }
}

// Profile image — sized off the smaller of available height/width so it
// always leaves room for the text stacked below it, on any viewport.
.profile-container {
  position: relative;
  width: min(36vh, 22vw, 300px);
  aspect-ratio: 4/5;
  perspective: 1000px;
  margin: 0 auto;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: min(55vw, 280px);
  }
}

.profile-backdrop {
  position: absolute;
  top: 10%;
  left: 10%;
  width: 90%;
  height: 90%;
  background: rgba(var(--background-alt-rgb), 0.3);
  border-radius: 1rem;
  transform: translateZ(-50px);
  filter: blur(8px);
}

.profile-frame {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 1rem;
  overflow: hidden;
  transform-style: preserve-3d;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(var(--accent-rgb), 0.3);
  // Driven by two custom properties written once per frame, so the tilt is a
  // compositor transform rather than a tween per pointer event.
  transform: perspective(900px)
    rotateX(var(--tilt-x, 0deg))
    rotateY(var(--tilt-y, 0deg));
  transition: transform 0.15s ease-out;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.1) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    z-index: 2;
  }

  .profile-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    // Above the skeleton, so the two cross-fade rather than one replacing the
    // other.
    position: relative;
    z-index: 2;
    opacity: 0;
    transition: opacity 0.5s ease, transform 0.5s ease;

    &.is-ready {
      opacity: 1;
    }

    &:hover {
      transform: scale(1.05);
    }
  }

  // Holds the frame until we know which picture to show and it has loaded.
  .profile-skeleton {
    position: absolute;
    inset: 0;
    z-index: 1;
    background:
      linear-gradient(
        110deg,
        rgba(var(--muted-rgb), 0.10) 30%,
        rgba(var(--muted-rgb), 0.22) 50%,
        rgba(var(--muted-rgb), 0.10) 70%
      ),
      rgba(var(--muted-rgb), 0.12);
    background-size: 220% 100%;
    animation: profile-skeleton-sweep 1.6s ease-in-out infinite;
  }
}

.profile-glow {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(
    circle at center,
    rgba(var(--accent-rgb), 0.4) 0%,
    rgba(var(--accent-rgb), 0) 70%
  );
  opacity: 0.4;
  z-index: 1;
  transform: translate3d(var(--glow-x, 0px), var(--glow-y, 0px), 0);
  transition: transform 0.15s ease-out, opacity 0.15s ease-out;
  pointer-events: none;
}

// Particles
// Section headings step down noticeably on phones: the desktop clamp is tuned
// for a 100vh panel and reads oversized on a narrow screen.
@media (max-width: 768px) {
  .section-heading {
    gap: 0.6rem;

    .heading-line {
      width: 26px;
      height: 2px;
    }

    .section-title {
      font-size: clamp(0.95rem, 4.2vw, 1.25rem);
      letter-spacing: 2px;
    }
  }
}
</style>
